import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-universidade-do-carro',
  templateUrl: './universidade-do-carro.component.html',
  styleUrls: ['./universidade-do-carro.component.scss']
})
export class UniversidadeDoCarroComponent implements OnInit, OnDestroy {

  heroCtaHref = 'https://universidadedocarro.com.br/?utm_source=union&utm_medium=organic&utm_campaign=parceria_universidade&utm_content=cta_hero';
  whatsappPhone = '+5511963056359';
  whatsappHref = 'https://api.whatsapp.com/send?phone=5511963056359&text=Quero%20saber%20sobre%20a%20Universidade%20do%20Carro';
  consentKey = 'uc_analytics_consent';
  consentGranted = false;
  showConsentBanner = false;
  private gaLoaded = false;

  constructor(
    private title: Title,
    private meta: Meta,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit() {
    this.setHeadTags();
    this.setCanonical();
    this.injectJsonLd();
    this.initConsent();
  }

  ngOnDestroy() {
    this.removeInjectedJsonLd();
  }

  trackCta(location: 'hero' | 'faq_cta') {
    if (this.consentGranted && (window as any).gtag) {
      (window as any).gtag('event', 'cta_click', {
        event_category: 'engagement',
        event_label: 'acessar_universidade',
        location
      });
    }
  }

  trackWhatsApp(location: 'hero' | 'footer') {
    if (this.consentGranted && (window as any).gtag) {
      (window as any).gtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: 'whatsapp_universidade_do_carro',
        location,
        phone: this.whatsappPhone
      });
    }
  }

  acceptAnalytics() {
    this.consentGranted = true;
    localStorage.setItem(this.consentKey, 'granted');
    this.showConsentBanner = false;
    this.loadGaIfAvailable();
  }

  refuseAnalytics() {
    this.consentGranted = false;
    localStorage.setItem(this.consentKey, 'denied');
    this.showConsentBanner = false;
  }

  private initConsent() {
    const saved = localStorage.getItem(this.consentKey);
    if (saved === 'granted') {
      this.consentGranted = true;
      this.showConsentBanner = false;
      this.loadGaIfAvailable();
    } else if (saved === 'denied') {
      this.consentGranted = false;
      this.showConsentBanner = false;
    } else {
      this.showConsentBanner = true;
    }
  }

  private loadGaIfAvailable() {
    const measurementId = (environment as any).gaMeasurementId || '';
    if (!measurementId || this.gaLoaded) {
      return;
    }

    const scriptId = 'ga4-gtag';
    if (this.document.getElementById(scriptId)) {
      this.gaLoaded = true;
      return;
    }

    const script = this.renderer.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.id = scriptId;
    this.renderer.appendChild(this.document.head, script);

    const inline = this.renderer.createElement('script');
    inline.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}', { 'anonymize_ip': true });
    `;
    this.renderer.appendChild(this.document.head, inline);
    this.gaLoaded = true;
  }

  private setHeadTags() {
    const title = 'Universidade do Carro | Capacitação em Vistoria Veicular | Union Solutions';
    const description = 'Curso livre com simuladores realistas, avaliação objetiva e certificação com QR Code para inspetores, ECVs, seguradoras e frotistas.';
    const url = 'https://unionsolutions.com.br/solucao/universidade-do-carro';
    const image = 'https://siteunion.s3.sa-east-1.amazonaws.com/wp-content/uploads/2021/04/Vistoria_1920x324-1.png';
    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'keywords', content: 'curso vistoria veicular, capacitação vistoriador, treinamento vistoria cautelar, curso para ECV, certificação vistoria veicular, simulador vistoria' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'robots', content: 'index,follow' });
  }

  private setCanonical() {
    const url = 'https://unionsolutions.com.br/solucao/universidade-do-carro';
    let link: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.renderer.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.renderer.appendChild(this.document.head, link);
    }
    link.setAttribute('href', url);
  }

  private injectJsonLd() {
    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-universidade-do-carro';
    const url = 'https://unionsolutions.com.br/solucao/universidade-do-carro';
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': 'Universidade do Carro | Capacitação em Vistoria Veicular',
      'url': url,
      'inLanguage': 'pt-BR',
      'description': 'Capacitação profissional em vistoria veicular com simuladores realistas, avaliação objetiva e certificação digital.',
      'breadcrumb': {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://unionsolutions.com.br/' },
          { '@type': 'ListItem', 'position': 2, 'name': 'Soluções', 'item': 'https://unionsolutions.com.br/solucoes' },
          { '@type': 'ListItem', 'position': 3, 'name': 'Universidade do Carro', 'item': url }
        ]
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Union Solutions',
        'url': 'https://unionsolutions.com.br',
        'logo': 'https://siteunion.s3.dualstack.sa-east-1.amazonaws.com/wp-content/uploads/Logo_Union_Branco.svg',
        'sameAs': [
          'https://www.instagram.com/union_solutions/',
          'https://www.facebook.com/UnionSolutionsHolding',
          'https://www.linkedin.com/company/union-solutions/'
        ]
      }
    };
    script.text = JSON.stringify(jsonLd);
    this.renderer.appendChild(this.document.head, script);
  }

  private removeInjectedJsonLd() {
    const existing = this.document.getElementById('jsonld-universidade-do-carro');
    if (existing) {
      existing.remove();
    }
  }
}
